import { AxiosRequest } from "_services/api";
import { HttpError } from "_services/api/HttpError";
import { HttpResponse } from "_services/api/HttpResponse";
import { IData, IHttpError } from "_services/api/interface/HttpResponse";
import { usePaginationSortSearch } from "containers/Table/hooks/usePaginationSortSearch";
import { useCallback, useState } from "react";
import { IListarLoteGerenciarPagamento } from "../interface/IListarLoteGerenciarPagamento";
import { IFiltrosGerenciarPagamento } from "../interface/IFiltrosGerenciarPagamento";
import { IDadosPagamento } from "../interface/IDadosPagamento";
import { IHistoricoLote } from "../interface/IHistoricoLote";
import { IResumoLoteGerenciarPagamento } from "../interface/IDetalheLoteGerenciarPagamento";
import { IRelatorioDetalhadoPagamento } from "../interface/IRelatorioDetalhadoPagamento";
import { Data } from "_utils/Data";
import { toastErro } from "components/Toast";
import { ISiglaDocumento } from "../containers/Tabs/TabAprovadosPagamento/constants/siglasDocumento";

const getMensagemErro = (error: any, fallback: string): string =>
  error?.response?.data?.message ?? fallback;

export function useAprovadosPagamento() {
  const msAw = AxiosRequest.makeApi(process.env.REACT_APP_MS_AW);
  const { totalRows, setTotalRows } = usePaginationSortSearch();
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState<IListarLoteGerenciarPagamento[]>([]);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(50);
  const [filtro, setFiltro] = useState<IFiltrosGerenciarPagamento>({});
  const [selectedRows, setSelectedRows] = useState<
    IListarLoteGerenciarPagamento[]
  >([]);
  const [clearRows, setClearRows] = useState<boolean>(false);
  const [idLoteEscolhido, setIdLoteEscolhido] = useState<number | null>(null);
  const [dadosDetalhes, setDadosDetalhes] =
    useState<IResumoLoteGerenciarPagamento | null>(null);
  const [pagamentoUrgenteAtivo, setPagamentoUrgenteAtivo] =
    useState<boolean>(false);
  const [ordenacao, setOrdenacao] = useState<{
    campo: string;
    direcao: string;
  } | null>(null);

  const buildParams = (
    obj: Record<string, any>,
    dadosFiltros: IFiltrosGerenciarPagamento,
    urgente?: boolean,
  ) => {
    if (dadosFiltros.idsLotes?.length) {
      obj.lotes = JSON.stringify(dadosFiltros.idsLotes);
    }
    if (dadosFiltros.idsNfRecibo?.length) {
      obj.nfRecibos = JSON.stringify(dadosFiltros.idsNfRecibo);
    }
    if (dadosFiltros.idsProcessos?.length) {
      obj.processos = JSON.stringify(dadosFiltros.idsProcessos);
    }
    if (dadosFiltros.idEmpresa) {
      obj.idEmpresa = dadosFiltros.idEmpresa;
    }
    if (dadosFiltros.idsBeneficiarios?.length) {
      obj.beneficiarios = JSON.stringify(dadosFiltros.idsBeneficiarios);
    }
    if (dadosFiltros.placa) {
      obj.placa = dadosFiltros.placa;
    }
    if (dadosFiltros.idsCustos?.length) {
      obj.custos = JSON.stringify(dadosFiltros.idsCustos);
    }
    if (dadosFiltros.idUsuario) {
      obj.idUsuario = dadosFiltros.idUsuario;
    }
    if (dadosFiltros.statusPagamento) {
      obj.statusPagamento = dadosFiltros.statusPagamento;
    }
    if (
      dadosFiltros.periodoAprovacao &&
      dadosFiltros.periodoAprovacao[0] &&
      dadosFiltros.periodoAprovacao[1]
    ) {
      obj.dataInicioAprovacao = new Data(
        dadosFiltros.periodoAprovacao[0],
      ).getDataEua();
      obj.dataFimAprovacao = new Data(
        dadosFiltros.periodoAprovacao[1],
      ).getDataEua();
    }
    if (
      dadosFiltros.periodoPagamento &&
      dadosFiltros.periodoPagamento[0] &&
      dadosFiltros.periodoPagamento[1]
    ) {
      obj.dataInicioPagamento = new Data(
        dadosFiltros.periodoPagamento[0],
      ).getDataEua();
      obj.dataFimPagamento = new Data(
        dadosFiltros.periodoPagamento[1],
      ).getDataEua();
    }
    if (urgente) {
      obj.pagamentoUrgente = true;
    }
    if (ordenacao?.campo && ordenacao?.direcao) {
      obj.ordenarPor = ordenacao.campo;
      obj.ordem = ordenacao.direcao.toUpperCase();
    }

    return Object.keys(obj)
      .map(
        (key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`,
      )
      .join("&");
  };

  const listar = useCallback(
    async (dadosFiltros?: IFiltrosGerenciarPagamento, urgente?: boolean) => {
      const filtroAtual = dadosFiltros ?? filtro;
      if (dadosFiltros !== undefined) {
        setFiltro(dadosFiltros);
      }

      setLoading(true);
      setClearRows(true);

      const params = buildParams(
        { pagina: page, limite: limit, statusAprovacao: "APROVADO" },
        filtroAtual,
        urgente ?? pagamentoUrgenteAtivo,
      );

      try {
        const { status, data } = await msAw.get(`/pagamentos/lote?${params}`);
        setDados(data.itens ?? []);
        setTotalRows(data.meta?.total ?? 0);
        return new HttpResponse(false, status, data);
      } catch (error: IHttpError<IData, any> | any) {
        setDados([]);
        setTotalRows(0);
        return new HttpError<IData>(true, error);
      } finally {
        setLoading(false);
        setClearRows(false);
      }
    },
    [page, limit, filtro, pagamentoUrgenteAtivo, ordenacao],
  );

  const ordenar = (campo: string, direcao: string) => {
    setOrdenacao(campo ? { campo, direcao } : null);
  };

  const relatorioDetalhado = async (): Promise<
    IRelatorioDetalhadoPagamento[] | null
  > => {
    setLoading(true);
    try {
      const dadosFiltros = selectedRows.length
        ? { ...filtro, idsLotes: selectedRows.map((row) => row.id) }
        : filtro;
      const params = buildParams(
        { statusAprovacao: "APROVADO" },
        dadosFiltros,
        pagamentoUrgenteAtivo,
      );
      const { data } = await msAw.get(
        `/pagamentos/lote/relatorio/detalhado?${params}`,
      );
      return data;
    } catch (error) {
      toastErro(
        getMensagemErro(error, "Não foi possível gerar o relatório detalhado"),
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  const buscarDetalhesLote = useCallback(async (idLote: number) => {
    setLoading(true);
    setDadosDetalhes(null);
    try {
      const { status, data } = await msAw.get(
        `/pagamentos/lote/${idLote}/detalhe`,
      );
      setDadosDetalhes(data);
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      toastErro(getMensagemErro(error, "Erro ao buscar detalhes do lote"));
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  }, []);

  const validarLotes = useCallback(
    async (observacao: string) => {
      const ids = selectedRows.map((row) => row.id);
      setLoading(true);
      try {
        const { status, data } = await msAw.post(`/pagamentos/lote/validar`, {
          ids,
          observacao,
        });
        return new HttpResponse(false, status, data);
      } catch (error: IHttpError<IData, any> | any) {
        toastErro(getMensagemErro(error, "Erro ao validar lotes"));
        return new HttpError<IData>(true, error);
      } finally {
        setLoading(false);
      }
    },
    [selectedRows],
  );

  const solicitarCorrecao = useCallback(
    async (motivo: string, descricao: string) => {
      const ids = selectedRows.map((row) => row.id);
      setLoading(true);
      try {
        const { status, data } = await msAw.post(
          `/pagamentos/lote/solicitar-correcao`,
          {
            ids,
            motivo,
            descricao,
          },
        );
        return new HttpResponse(false, status, data);
      } catch (error: IHttpError<IData, any> | any) {
        toastErro(getMensagemErro(error, "Erro ao solicitar correção"));
        return new HttpError<IData>(true, error);
      } finally {
        setLoading(false);
      }
    },
    [selectedRows],
  );

  const confirmarPagamentoFornecedor = async (
    idsLotes: number[],
    dataPagamento: string,
    arquivos: File[],
    siglaComprovante?: string,
  ) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("idsLotes", JSON.stringify(idsLotes));
      formData.append("dataPagamento", dataPagamento);
      if (siglaComprovante) {
        formData.append("siglaComprovante", siglaComprovante);
      }
      (arquivos ?? []).forEach((arquivo) =>
        formData.append("comprovantes", arquivo),
      );

      const { status, data } = await msAw.post(
        `/pagamentos/lote/confirmar-pagamento-fornecedor`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      toastErro(getMensagemErro(error, "Erro ao confirmar pagamento"));
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  };

  const confirmarPagamentoTaxa = async (
    idsLotes: number[],
    dataPagamento: string,
    arquivos: File[],
  ) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("idsLotes", JSON.stringify(idsLotes));
      formData.append("dataPagamento", dataPagamento);
      (arquivos ?? []).forEach((arquivo) =>
        formData.append("comprovantes", arquivo),
      );

      const { status, data } = await msAw.post(
        `/pagamentos/lote/confirmar-pagamento-taxa`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      toastErro(getMensagemErro(error, "Erro ao confirmar pagamento"));
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  };

  const buscarSiglasDocumento = async (): Promise<ISiglaDocumento[]> => {
    try {
      const { data } = await msAw.get(`/pagamentos/lote/siglas-taxa`);
      return data ?? [];
    } catch (error) {
      toastErro(getMensagemErro(error, "Erro ao buscar as siglas"));
      return [];
    }
  };

  const anexarRecibo = async (idLote: number, arquivo: File) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("arquivoRecibo", arquivo);
      const { status, data } = await msAw.post(
        `/pagamentos/lote/${idLote}/recibo`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      toastErro(
        getMensagemErro(error, "Erro ao anexar o arquivo de recibo/NF"),
      );
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  };

  const buscarDadosPagamento = async (
    idLote: number,
  ): Promise<IDadosPagamento | null> => {
    setLoading(true);
    try {
      const { data } = await msAw.get(
        `/pagamentos/lote/${idLote}/dados-pagamento`,
      );
      return data;
    } catch (error) {
      toastErro(getMensagemErro(error, "Erro ao buscar dados para pagamento"));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const buscarHistorico = async (
    idLote: number,
  ): Promise<IHistoricoLote | null> => {
    setLoading(true);
    try {
      const { data } = await msAw.get(`/pagamentos/lote/${idLote}/historico`);
      return data;
    } catch (error) {
      toastErro(getMensagemErro(error, "Erro ao buscar histórico do lote"));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const adicionarObservacao = async (idLote: number, observacao: string) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.post(
        `/pagamentos/lote/${idLote}/observacao`,
        {
          observacao,
        },
      );
      return new HttpResponse(false, status, data);
    } catch (error: IHttpError<IData, any> | any) {
      toastErro(getMensagemErro(error, "Erro ao salvar observação"));
      return new HttpError<IData>(true, error);
    } finally {
      setLoading(false);
    }
  };

  const baixarReciboNf = async (idLote: number, numeroNfRecibo: string) => {
    setLoading(true);
    try {
      const { data, headers } = await msAw.get(
        `/pagamentos/lote/${idLote}/imagem/recibo`,
        {
          responseType: "blob",
        },
      );
      const contentType = headers["content-type"] || "application/octet-stream";
      const ext = contentType.split("/").pop()?.split(";")[0] ?? "bin";
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `recibo_nf_${numeroNfRecibo}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toastErro(getMensagemErro(error, "Não foi possível baixar o arquivo"));
    } finally {
      setLoading(false);
    }
  };

  const baixarComprovante = async (idLote: number) => {
    setLoading(true);
    try {
      const { data, headers } = await msAw.get(
        `/pagamentos/lote/${idLote}/comprovante`,
        {
          responseType: "blob",
        },
      );
      const contentType = headers["content-type"] || "application/octet-stream";
      const disposition: string = headers["content-disposition"] ?? "";
      const match = disposition.match(/filename=([^;]+)/);
      const ext = contentType.includes("zip")
        ? "zip"
        : (contentType.split("/").pop()?.split(";")[0] ?? "bin");
      const filename = match ? match[1].trim() : `comprovante_${idLote}.${ext}`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toastErro(
        getMensagemErro(error, "Não foi possível baixar o comprovante"),
      );
    } finally {
      setLoading(false);
    }
  };

  const visualizarBoleto = async (idLote: number, idDocumento: number) => {
    try {
      const { data, headers } = await msAw.get(
        `/pagamentos/lote/${idLote}/boleto/${idDocumento}`,
        {
          responseType: "blob",
        },
      );
      const contentType = headers["content-type"] || "application/octet-stream";
      const url = window.URL.createObjectURL(
        new Blob([data], { type: contentType }),
      );
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toastErro(getMensagemErro(error, "Não foi possível abrir o boleto"));
    }
  };

  const baixarBoleto = async (
    idLote: number,
    idDocumento: number,
    nome: string,
  ) => {
    setLoading(true);
    try {
      const { data, headers } = await msAw.get(
        `/pagamentos/lote/${idLote}/boleto/${idDocumento}`,
        {
          responseType: "blob",
        },
      );
      const contentType = headers["content-type"] || "application/octet-stream";
      const disposition: string = headers["content-disposition"] ?? "";
      const match = disposition.match(/filename=([^;]+)/);
      const ext = contentType.split("/").pop()?.split(";")[0] ?? "bin";
      const nomeBase = nome || `boleto_${idDocumento}`;
      const filename = match
        ? match[1].trim()
        : /\.[a-z0-9]{1,5}$/i.test(nomeBase)
          ? nomeBase
          : `${nomeBase}.${ext}`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toastErro(getMensagemErro(error, "Não foi possível baixar o boleto"));
    } finally {
      setLoading(false);
    }
  };

  const baixarTodosBoletos = async (idLote: number) => {
    setLoading(true);
    try {
      const { data, headers } = await msAw.get(
        `/pagamentos/lote/${idLote}/boletos/zip`,
        {
          responseType: "blob",
        },
      );
      const disposition: string = headers["content-disposition"] ?? "";
      const match = disposition.match(/filename=([^;]+)/);
      const filename = match ? match[1].trim() : `boletos_${idLote}.zip`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toastErro(getMensagemErro(error, "Não foi possível baixar os boletos"));
    } finally {
      setLoading(false);
    }
  };

  const onClickProcesso = (param: string) => {
    window.open(
      `${process.env.REACT_APP_DESPACHANTE_BASE_URL}/pages/processo/Processo.php?param=${param}`,
      "_blank",
    );
  };

  const togglePagamentoUrgente = (urgente: boolean) => {
    setPagamentoUrgenteAtivo(urgente);
    listar(filtro, urgente);
  };

  return {
    dados,
    totalRows,
    limit,
    page,
    loading,
    filtro,
    setLimit,
    setPage,
    listar,
    relatorioDetalhado,
    selectedRows,
    setSelectedRows,
    clearRows,
    idLoteEscolhido,
    setIdLoteEscolhido,
    dadosDetalhes,
    setDadosDetalhes,
    buscarDetalhesLote,
    validarLotes,
    solicitarCorrecao,
    confirmarPagamentoFornecedor,
    confirmarPagamentoTaxa,
    buscarSiglasDocumento,
    anexarRecibo,
    buscarDadosPagamento,
    buscarHistorico,
    adicionarObservacao,
    baixarReciboNf,
    baixarComprovante,
    visualizarBoleto,
    baixarBoleto,
    baixarTodosBoletos,
    onClickProcesso,
    pagamentoUrgenteAtivo,
    togglePagamentoUrgente,
    ordenacao,
    ordenar,
  };
}
